'use client';

import { useEffect, useMemo, useState } from 'react';
import type { WebGrpcDecodedData } from '@terra/shared-schemas-tracker';
import {
  isGrpcHarDecodeComplete,
  mergeWebGrpcDecodedData,
  type StoredHarDisplayEntry,
} from '@terra/shared-utils';
import { buildHarDisplayEditorProps } from './build-har-display-editor-props';
import { useHarEntryHttpDisplay } from './useHarEntryHttpDisplay';

export type HarPreviewDecodeFn = (params: {
  applicationId: string;
  harEntry: StoredHarDisplayEntry;
}) => Promise<WebGrpcDecodedData | null>;

export function useHarEntryDisplayWithPreview({
  harEntry: baseHarEntry,
  applicationId,
  viewResetKey,
  decodePreview,
  protocolDecodeEnabled = true,
}: {
  harEntry?: StoredHarDisplayEntry | null;
  applicationId?: string;
  viewResetKey?: string;
  decodePreview?: HarPreviewDecodeFn;
  protocolDecodeEnabled?: boolean;
}) {
  const [previewDecodedData, setPreviewDecodedData] =
    useState<WebGrpcDecodedData | null>(null);

  useEffect(() => {
    setPreviewDecodedData(null);
  }, [viewResetKey]);

  const harEntry = useMemo((): StoredHarDisplayEntry | null => {
    if (!baseHarEntry) {
      return null;
    }

    const mergedDecode = mergeWebGrpcDecodedData({
      base: baseHarEntry.webGrpcDecodedData,
      overlay: previewDecodedData,
    });

    if (!mergedDecode) {
      return baseHarEntry;
    }

    return {
      ...baseHarEntry,
      webGrpcDecodedData: mergedDecode,
    };
  }, [baseHarEntry, previewDecodedData]);

  const display = useHarEntryHttpDisplay({
    harEntry: harEntry ?? {
      request: {},
      response: null,
      webGrpcDecodedData: null,
    },
    viewResetKey,
  });

  const showProtocolBodyViewToggle =
    display.hasDecodedBodyView && protocolDecodeEnabled;

  useEffect(() => {
    if (
      !protocolDecodeEnabled ||
      !applicationId ||
      !baseHarEntry ||
      !decodePreview ||
      previewDecodedData !== null ||
      isGrpcHarDecodeComplete(baseHarEntry.webGrpcDecodedData)
    ) {
      return;
    }

    let cancelled = false;

    void decodePreview({ applicationId, harEntry: baseHarEntry })
      .then((overlay) => {
        if (cancelled || !overlay) {
          return;
        }

        setPreviewDecodedData(overlay);
      })
      .catch((error: unknown) => {
        console.warn('Failed to preview-decode HAR entry for display', {
          error: error instanceof Error ? error : new Error(String(error)),
        });
      });

    return () => {
      cancelled = true;
    };
  }, [
    applicationId,
    baseHarEntry,
    decodePreview,
    previewDecodedData,
    protocolDecodeEnabled,
    viewResetKey,
  ]);

  const editorProps = buildHarDisplayEditorProps({
    showProtocolBodyViewToggle,
    bodyView: display.bodyView,
    onBodyViewChange: display.setBodyView,
  });

  return {
    harEntry,
    bodyView: display.bodyView,
    setBodyView: display.setBodyView,
    hasDecodedBodyView: display.hasDecodedBodyView,
    requestContent: harEntry ? display.requestContent : '',
    responseContent: harEntry ? display.responseContent : '',
    showProtocolBodyViewToggle,
    editorProps,
  };
}
